import { useEffect, useRef, useState } from "react";
import { FolderOpen, Upload, ChevronDown, Plus, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "../lib/api";
import { FadeUp } from "../components/Reveal";

function AdminProjectCard({ project }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState(null);
  const [form, setForm] = useState({ name: "", version: "v1", kind: "mix" });
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get(`/projects/${project.project_id}/files`).then((r) => setFiles(r.data)).catch(() => {});
  const toggle = () => {
    if (!open && files === null) load();
    setOpen(!open);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("name", form.name || f.name);
      fd.append("version", form.version);
      fd.append("kind", form.kind);
      await api.post(`/projects/${project.project_id}/files`, fd);
      toast.success("File delivered — client texted");
      setForm({ ...form, name: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const toggleVis = async (fileId) => {
    await api.patch(`/files/${fileId}/visibility`);
    load();
  };

  return (
    <FadeUp className="bg-white rounded-2xl border border-purple-100 overflow-hidden" data-testid={`admin-project-${project.project_id}`}>
      <button onClick={toggle} className="w-full flex items-center gap-4 p-6 text-left" data-testid={`admin-project-toggle-${project.project_id}`}>
        <span className="w-11 h-11 rounded-xl bg-lilac-mist flex items-center justify-center text-purple-700 shrink-0"><FolderOpen size={18} /></span>
        <span className="flex-1 min-w-0">
          <span className="block font-medium text-obsidian truncate">{project.title}</span>
          <span className="block text-xs text-slate-400 mt-0.5">{project.client_name} · {project.artist} · {project.status}</span>
        </span>
        <ChevronDown size={17} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-purple-50">
          <div className="mt-5 space-y-2">
            {(files || []).map((f) => (
              <div key={f.file_id} className="flex items-center gap-3 text-sm p-3 rounded-xl bg-lilac-mist" data-testid={`admin-file-${f.file_id}`}>
                <span className="flex-1 truncate">{f.name}</span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-purple-700">{f.kind} · {f.version}</span>
                <a href={fileUrl(f.file_id, "download")} className="text-xs text-slate-500 hover:text-gold" data-testid={`admin-download-${f.file_id}`}>Get</a>
                <button onClick={() => toggleVis(f.file_id)} data-testid={`admin-visibility-${f.file_id}`}
                  className={`flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${f.visible_to_client ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                  {f.visible_to_client ? <><Eye size={10} /> client</> : <><EyeOff size={10} /> hidden</>}
                </button>
              </div>
            ))}
            {files && files.length === 0 && <p className="text-sm text-slate-400 italic">No files yet.</p>}
          </div>
          <div className="flex flex-wrap items-end gap-3 mt-5 pt-5 border-t border-purple-50">
            <input placeholder="File label (e.g. Final Mix v3)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid={`upload-name-${project.project_id}`}
              className="flex-1 min-w-[180px] rounded-xl border border-purple-100 px-3 py-2 text-sm" />
            <input placeholder="Version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })}
              data-testid={`upload-version-${project.project_id}`}
              className="w-24 rounded-xl border border-purple-100 px-3 py-2 text-sm" />
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid={`upload-kind-${project.project_id}`}
              className="rounded-xl border border-purple-100 px-3 py-2 text-sm bg-white">
              {["mix", "master", "instrumental", "acapella", "stem", "other"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <input ref={inputRef} type="file" className="hidden" onChange={upload} data-testid={`upload-input-${project.project_id}`} />
            <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`upload-btn-${project.project_id}`}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-obsidian text-white text-xs hover:text-gold transition-colors disabled:opacity-50">
              <Upload size={13} /> {uploading ? "Delivering…" : "Deliver file"}
            </button>
          </div>
        </div>
      )}
    </FadeUp>
  );
}

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ user_id: "", title: "", artist: "", notes: "" });

  const load = () => api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/projects", form);
      toast.success("Project created");
      setCreating(false);
      setForm({ user_id: "", title: "", artist: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-projects">
      <FadeUp className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl text-obsidian">Projects</h1>
          <p className="text-sm text-slate-500 mt-2">Create projects, deliver mixes and masters, control what clients see.</p>
        </div>
        <button onClick={() => setCreating(!creating)} data-testid="new-project-toggle"
          className="flex items-center gap-2 px-5 py-3 rounded-full bg-obsidian text-white text-sm hover:text-gold transition-colors">
          <Plus size={15} /> New project
        </button>
      </FadeUp>

      {creating && (
        <FadeUp className="mt-6 bg-white rounded-2xl border border-purple-100 p-6" data-testid="new-project-form">
          <form onSubmit={create} className="grid gap-3 sm:grid-cols-2">
            <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} required data-testid="new-project-client"
              className="rounded-xl border border-purple-100 px-3 py-2.5 text-sm bg-white">
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.user_id} value={c.user_id}>{c.name} ({c.email})</option>)}
            </select>
            <input placeholder="Project title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required
              data-testid="new-project-title" className="rounded-xl border border-purple-100 px-3 py-2.5 text-sm" />
            <input placeholder="Artist name" value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })}
              data-testid="new-project-artist" className="rounded-xl border border-purple-100 px-3 py-2.5 text-sm" />
            <input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="new-project-notes" className="rounded-xl border border-purple-100 px-3 py-2.5 text-sm" />
            <button type="submit" data-testid="new-project-submit"
              className="sm:col-span-2 px-6 py-3 rounded-full bg-gold text-obsidian text-sm font-semibold hover:brightness-110 transition-all">
              Create project
            </button>
          </form>
        </FadeUp>
      )}

      <div className="mt-8 space-y-4">
        {projects.map((p) => <AdminProjectCard key={p.project_id} project={p} />)}
        {projects.length === 0 && <p className="text-sm text-slate-400 italic">No projects yet.</p>}
      </div>
    </div>
  );
}
